import * as React from 'react'
import type { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { Calendar } from '#/shared/ui/calendar'
import { FilterSlidersIcon } from '#/shared/ui/icons'
import type { MultiSelectOption } from '#/shared/ui/multi-select'
import { InlineOptionList } from './inline-option-list'
import { Popover } from '#/shared/ui/popover'
import { Select } from '#/shared/ui/select'
import type { SelectOption } from '#/shared/ui/select'

interface FilterOption {
  value: string
  label: string
  type?: 'select' | 'dateRange' | 'search' | 'singleSelect'
  placeholder?: string
  options?: MultiSelectOption[]
  max?: number
  /** `search` panels whose options are fetched — shows the option skeleton. */
  loading?: boolean
}

interface FilterGroup {
  items: FilterOption[]
}

interface FiltersButtonProps {
  count?: number
  onClick?: () => void
  groups?: FilterGroup[]
  value?: string
  onValueChange?: (value: string) => void
  dateRanges?: Record<string, DateRange | undefined>
  onDateRangeApply?: (key: string, range: DateRange | undefined) => void
  searchValues?: Record<string, string[] | undefined>
  onSearchApply?: (key: string, value: string[] | undefined) => void
  selectValues?: Record<string, string | undefined>
  onSelectApply?: (key: string, value: string | undefined) => void
  onClearAll?: () => void
  disabled?: boolean
  /**
   * Plain-mode (no `groups`) disclosure semantics: set when the button
   * toggles an inline panel so assistive tech hears the open state and can
   * find the controlled region.
   */
  ariaExpanded?: boolean
  ariaControls?: string
  /**
   * Controlled popover open state. Lets a sibling control (e.g. the
   * chip-row `AddFilterButton`) re-open the same popover. When omitted,
   * the Popover manages its own open state as usual.
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

function CountBadge({ count }: { count: number }) {
  return (
    <span
      key={count}
      className="flex h-4.5 min-w-4.5 shrink-0 items-center justify-center rounded-full bg-primary px-1 text-xs leading-none text-white animate-pop-in"
    >
      {count}
    </span>
  )
}

function TriggerInner({ count, label }: { count?: number; label: string }) {
  return (
    <>
      <FilterSlidersIcon className="size-4.5" />
      {label}
      {count != null && count > 0 && <CountBadge count={count} />}
    </>
  )
}

interface ActiveStateMaps {
  value: string | undefined
  dateRanges: Record<string, DateRange | undefined> | undefined
  searchValues: Record<string, string[] | undefined> | undefined
  selectValues: Record<string, string | undefined> | undefined
}

const ACTIVE_CHECKS: Record<
  Exclude<NonNullable<FilterOption['type']>, 'select'>,
  (item: FilterOption, maps: ActiveStateMaps) => boolean
> = {
  dateRange: (item, { dateRanges }) => {
    const range = dateRanges?.[item.value]
    return Boolean(range?.from ?? range?.to)
  },
  search: (item, { searchValues }) => {
    const values = searchValues?.[item.value]
    return Boolean(values && values.length > 0)
  },
  singleSelect: (item, { selectValues }) => Boolean(selectValues?.[item.value]),
}

// Per-item "has an applied value" check. Each filter kind stores its
// applied state in a different place; centralising the lookup here keeps
// the render loop free of type-switching.
function isFilterItemActive(
  item: FilterOption,
  maps: ActiveStateMaps,
): boolean {
  const check =
    item.type && item.type !== 'select' ? ACTIVE_CHECKS[item.type] : undefined
  if (check) return check(item, maps)
  return item.value === maps.value
}

function FilterPanel({
  groups,
  value,
  activeItem,
  open,
  onSelect,
  dateRanges,
  searchValues,
  selectValues,
  onClearAll,
  hasAnyActive,
}: {
  groups: FilterGroup[]
  value?: string
  activeItem: string | null
  open: boolean
  onSelect: (item: FilterOption, el: HTMLButtonElement) => void
  dateRanges?: Record<string, DateRange | undefined>
  searchValues?: Record<string, string[] | undefined>
  selectValues?: Record<string, string | undefined>
  onClearAll?: () => void
  hasAnyActive: boolean
}) {
  const { t } = useTranslation()
  const firstItemRef = React.useRef<HTMLButtonElement | null>(null)
  React.useEffect(() => {
    if (open && !activeItem) firstItemRef.current?.focus()
  }, [open, activeItem])
  let buttonIndex = 0
  const showClearAll = Boolean(onClearAll) && hasAnyActive
  return (
    <div className="flex w-50 flex-col gap-1 rounded-md bg-white p-1 shadow-popover">
      {groups.map((group, gi) => (
        <React.Fragment key={gi}>
          {gi > 0 && <hr className="border-t border-gray-200" />}
          <div className="flex flex-col gap-0.5">
            {group.items.map((item) => {
              const highlighted = activeItem
                ? item.value === activeItem
                : item.value === value
              const isActive = isFilterItemActive(item, {
                value,
                dateRanges,
                searchValues,
                selectValues,
              })
              const isFirst = buttonIndex === 0
              buttonIndex += 1
              return (
                <button
                  key={item.value}
                  ref={isFirst ? firstItemRef : undefined}
                  type="button"
                  onClick={(e) => onSelect(item, e.currentTarget)}
                  className={cn(
                    'flex h-8 w-full cursor-pointer items-center rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-warm-50 focus:bg-warm-50 focus:outline-none',
                    highlighted && 'bg-warm-50',
                  )}
                >
                  {item.label}
                  {isActive && (
                    <span
                      aria-hidden
                      className="ml-auto size-1.5 shrink-0 rounded-full bg-primary"
                    />
                  )}
                </button>
              )
            })}
          </div>
        </React.Fragment>
      ))}
      {showClearAll && (
        <>
          <hr className="border-t border-gray-200" />
          <button
            type="button"
            onClick={onClearAll}
            className="flex h-8 w-full cursor-pointer items-center rounded-sm px-2.5 text-left text-sm text-destructive hover:bg-warm-50 focus:bg-warm-50 focus:outline-none"
          >
            {t('toolbar.clearAll')}
          </button>
        </>
      )}
    </div>
  )
}

function DateRangeSubPanel({
  initialRange,
  onCancel,
  onApply,
}: {
  initialRange?: DateRange
  onCancel: () => void
  onApply: (range: DateRange | undefined) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = React.useState<DateRange | undefined>(initialRange)
  const hasInitial = Boolean(initialRange?.from ?? initialRange?.to)
  const canApply = Boolean(draft?.from && draft.to)
  return (
    <div className="flex w-fit flex-col gap-2.5 rounded-md bg-white p-2.5 shadow-popover">
      <Calendar
        mode="range"
        captionLayout="dropdown"
        numberOfMonths={2}
        selected={draft}
        onSelect={setDraft}
        startMonth={new Date(1960, 0)}
        endMonth={new Date(new Date().getFullYear() + 10, 11)}
        className="p-0"
        autoFocus
      />
      <hr className="border-t border-gray-200" />
      <div className="flex items-stretch gap-2.5">
        <Button
          type="button"
          variant="outline"
          size="xs"
          className="flex-1"
          onClick={onCancel}
        >
          {t('toolbar.cancel')}
        </Button>
        {hasInitial && (
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="flex-1"
            onClick={() => onApply(undefined)}
          >
            {t('toolbar.clear')}
          </Button>
        )}
        <Button
          type="button"
          size="xs"
          className="flex-1"
          disabled={!canApply}
          onClick={() => onApply(draft)}
        >
          {t('toolbar.applyFilter')}
        </Button>
      </div>
    </div>
  )
}

function SearchSubPanel({
  initialValue,
  placeholder,
  options,
  max,
  loading,
  onCancel,
  onApply,
}: {
  initialValue?: string[]
  placeholder?: string
  options: MultiSelectOption[]
  max?: number
  loading?: boolean
  onCancel: () => void
  onApply: (value: string[] | undefined) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = React.useState<string[]>(initialValue ?? [])
  const hasInitial = Boolean(initialValue && initialValue.length > 0)
  // Clearing an applied filter is a valid apply; an empty draft with no
  // applied value is not.
  const canApply = draft.length > 0 || hasInitial
  return (
    <div className="flex w-74 flex-col gap-2.5 rounded-md bg-white p-2.5 shadow-popover">
      <InlineOptionList
        options={options}
        value={draft}
        onValueChange={setDraft}
        placeholder={placeholder}
        max={max}
        loading={loading}
      />
      <hr className="border-t border-gray-200" />
      <div className="flex items-stretch gap-2.5">
        <Button
          type="button"
          variant="outline"
          size="xs"
          className="w-20"
          onClick={onCancel}
        >
          {t('toolbar.cancel')}
        </Button>
        {hasInitial && (
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="flex-1"
            onClick={() => onApply(undefined)}
          >
            {t('toolbar.clear')}
          </Button>
        )}
        <Button
          type="button"
          size="xs"
          className="flex-1"
          disabled={!canApply}
          onClick={() => onApply(draft)}
        >
          {t('toolbar.applyFilter')}
        </Button>
      </div>
    </div>
  )
}

function SelectSubPanel({
  initialValue,
  placeholder,
  options,
  onCancel,
  onApply,
}: {
  initialValue?: string
  placeholder?: string
  options: SelectOption[]
  onCancel: () => void
  onApply: (value: string | undefined) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = React.useState<string | undefined>(initialValue)
  const hasInitial = Boolean(initialValue)
  const canApply = Boolean(draft)
  return (
    <div className="flex w-74 flex-col gap-2.5 rounded-md bg-white p-2.5 shadow-popover">
      <Select
        options={options}
        value={draft}
        onValueChange={setDraft}
        placeholder={placeholder}
      />
      <hr className="border-t border-gray-200" />
      <div className="flex items-stretch gap-2.5">
        <Button
          type="button"
          variant="outline"
          size="xs"
          className="w-20"
          onClick={onCancel}
        >
          {t('toolbar.cancel')}
        </Button>
        {hasInitial && (
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="flex-1"
            onClick={() => onApply(undefined)}
          >
            {t('toolbar.clear')}
          </Button>
        )}
        <Button
          type="button"
          size="xs"
          className="flex-1"
          disabled={!canApply}
          onClick={() => onApply(draft)}
        >
          {t('toolbar.applyFilter')}
        </Button>
      </div>
    </div>
  )
}

function PlainButton({
  count,
  label,
  onClick,
  disabled,
  ariaExpanded,
  ariaControls,
}: {
  count?: number
  label: string
  onClick?: () => void
  disabled?: boolean
  ariaExpanded?: boolean
  ariaControls?: string
}) {
  return (
    <Button
      variant="outline"
      size="xs"
      onClick={onClick}
      disabled={disabled}
      aria-expanded={ariaExpanded}
      aria-controls={ariaExpanded ? ariaControls : undefined}
    >
      <TriggerInner count={count} label={label} />
    </Button>
  )
}

function findItem(groups: FilterGroup[], value: string | null) {
  if (!value) return null
  for (const g of groups) {
    const found = g.items.find((i) => i.value === value)
    if (found) return found
  }
  return null
}

export function FiltersButton({
  count,
  onClick,
  groups,
  value,
  onValueChange,
  dateRanges,
  onDateRangeApply,
  searchValues,
  onSearchApply,
  selectValues,
  onSelectApply,
  onClearAll,
  disabled,
  ariaExpanded,
  ariaControls,
  open,
  onOpenChange,
}: FiltersButtonProps) {
  const { t } = useTranslation()
  const [activeItem, setActiveItem] = React.useState<string | null>(null)
  const [activeOffset, setActiveOffset] = React.useState(0)
  const label = t('toolbar.filters')

  // When disabled we also skip the Popover — its wrapper div has a click
  // handler that would still fire on a disabled-looking button.
  if (disabled || !groups)
    return (
      <PlainButton
        count={count}
        label={label}
        onClick={onClick}
        disabled={disabled}
        ariaExpanded={ariaExpanded}
        ariaControls={ariaControls}
      />
    )

  const handleSelect = (
    item: FilterOption,
    close: () => void,
    el: HTMLButtonElement,
  ) => {
    if (
      item.type === 'dateRange' ||
      item.type === 'search' ||
      item.type === 'singleSelect'
    ) {
      setActiveOffset(el.offsetTop)
      setActiveItem((prev) => (prev === item.value ? null : item.value))
      return
    }
    onValueChange?.(item.value)
    setActiveItem(null)
    close()
  }

  const active = findItem(groups, activeItem)

  return (
    <div className="w-fit">
      <Popover
        align="auto"
        side="auto"
        portal
        open={open}
        onOpenChange={onOpenChange}
        trigger={(popoverOpen) => (
          <Button
            variant="outline"
            size="xs"
            onClick={() => setActiveItem(null)}
            className={cn(
              'focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:focus-ring',
              popoverOpen && 'focus-ring',
            )}
          >
            <TriggerInner count={count} label={label} />
          </Button>
        )}
      >
        {(close, popoverOpen) => (
          <FiltersPopoverContent
            groups={groups}
            value={value}
            activeItem={activeItem}
            active={active}
            activeOffset={activeOffset}
            popoverOpen={popoverOpen}
            count={count ?? 0}
            dateRanges={dateRanges}
            searchValues={searchValues}
            selectValues={selectValues}
            onClearAll={onClearAll}
            onDateRangeApply={onDateRangeApply}
            onSearchApply={onSearchApply}
            onSelectApply={onSelectApply}
            onItemSelect={(item, el) => handleSelect(item, close, el)}
            setActiveItem={setActiveItem}
            close={close}
          />
        )}
      </Popover>
    </div>
  )
}

const SUBPANEL_VIEWPORT_MARGIN_PX = 8
const SUBPANEL_GAP_PX = 10

const SUBPANEL_SIDE_CLASSES: Record<'left' | 'right', string> = {
  left: 'right-full mr-2.5',
  right: 'left-full ml-2.5',
}

/**
 * Decides whether the active filter's sub-panel (e.g. the wide date calendar)
 * opens to the right or the left of the filter list. Defaults to the right and
 * only cascades left when the right would overflow the viewport but the left
 * fits — keeping the list anchored under its trigger either way.
 */
function useSubPanelSide(
  active: FilterOption | null,
  containerRef: React.RefObject<HTMLDivElement | null>,
  subRef: React.RefObject<HTMLDivElement | null>,
): 'left' | 'right' {
  const [side, setSide] = React.useState<'left' | 'right'>('right')
  React.useLayoutEffect(() => {
    if (!active) return
    const container = containerRef.current
    const sub = subRef.current
    if (!container || !sub) return
    const measure = () => {
      const rect = container.getBoundingClientRect()
      const needed = sub.offsetWidth + SUBPANEL_GAP_PX
      const roomRight =
        window.innerWidth - SUBPANEL_VIEWPORT_MARGIN_PX - rect.right
      const roomLeft = rect.left - SUBPANEL_VIEWPORT_MARGIN_PX
      setSide(needed > roomRight && roomLeft >= needed ? 'left' : 'right')
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [active, containerRef, subRef])
  return side
}

interface FiltersPopoverContentProps {
  groups: FilterGroup[]
  value?: string
  activeItem: string | null
  active: FilterOption | null
  activeOffset: number
  popoverOpen: boolean
  count: number
  dateRanges?: Record<string, DateRange | undefined>
  searchValues?: Record<string, string[] | undefined>
  selectValues?: Record<string, string | undefined>
  onClearAll?: () => void
  onDateRangeApply?: (key: string, range: DateRange | undefined) => void
  onSearchApply?: (key: string, value: string[] | undefined) => void
  onSelectApply?: (key: string, value: string | undefined) => void
  onItemSelect: (item: FilterOption, el: HTMLButtonElement) => void
  setActiveItem: (value: string | null) => void
  close: () => void
}

function FiltersPopoverContent({
  groups,
  value,
  activeItem,
  active,
  activeOffset,
  popoverOpen,
  count,
  dateRanges,
  searchValues,
  selectValues,
  onClearAll,
  onDateRangeApply,
  onSearchApply,
  onSelectApply,
  onItemSelect,
  setActiveItem,
  close,
}: FiltersPopoverContentProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const subRef = React.useRef<HTMLDivElement | null>(null)
  const subPanelSide = useSubPanelSide(active, containerRef, subRef)
  const dismissActive = () => setActiveItem(null)
  const applyAndClose = (run: () => void) => {
    run()
    setActiveItem(null)
    close()
  }
  return (
    // The list is the anchor; the sub-panel floats out of flow (absolute) so
    // the popover stays measured to the list and sits under its trigger.
    <div ref={containerRef} className="relative w-fit">
      <FilterPanel
        groups={groups}
        value={value}
        activeItem={activeItem}
        open={popoverOpen}
        onSelect={onItemSelect}
        dateRanges={dateRanges}
        searchValues={searchValues}
        selectValues={selectValues}
        hasAnyActive={count > 0}
        onClearAll={onClearAll ? () => applyAndClose(onClearAll) : undefined}
      />
      {active && (
        <ActiveSubPanel
          subRef={subRef}
          side={subPanelSide}
          active={active}
          activeOffset={activeOffset}
          dateRanges={dateRanges}
          searchValues={searchValues}
          selectValues={selectValues}
          onDateRangeApply={onDateRangeApply}
          onSearchApply={onSearchApply}
          onSelectApply={onSelectApply}
          dismissActive={dismissActive}
          applyAndClose={applyAndClose}
        />
      )}
    </div>
  )
}

interface ActiveSubPanelProps {
  active: FilterOption
  activeOffset: number
  side: 'left' | 'right'
  subRef: React.RefObject<HTMLDivElement | null>
  dateRanges?: Record<string, DateRange | undefined>
  searchValues?: Record<string, string[] | undefined>
  selectValues?: Record<string, string | undefined>
  onDateRangeApply?: (key: string, range: DateRange | undefined) => void
  onSearchApply?: (key: string, value: string[] | undefined) => void
  onSelectApply?: (key: string, value: string | undefined) => void
  dismissActive: () => void
  applyAndClose: (run: () => void) => void
}

function ActiveSubPanel({
  active,
  activeOffset,
  side,
  subRef,
  dateRanges,
  searchValues,
  selectValues,
  onDateRangeApply,
  onSearchApply,
  onSelectApply,
  dismissActive,
  applyAndClose,
}: ActiveSubPanelProps) {
  let body: React.ReactNode = null
  if (active.type === 'dateRange') {
    body = (
      <DateRangeSubPanel
        initialRange={dateRanges?.[active.value]}
        onCancel={dismissActive}
        onApply={(range) =>
          applyAndClose(() => onDateRangeApply?.(active.value, range))
        }
      />
    )
  } else if (active.type === 'search') {
    body = (
      <SearchSubPanel
        initialValue={searchValues?.[active.value]}
        placeholder={active.placeholder}
        options={active.options ?? []}
        max={active.max}
        loading={active.loading}
        onCancel={dismissActive}
        onApply={(next) =>
          applyAndClose(() => onSearchApply?.(active.value, next))
        }
      />
    )
  } else if (active.type === 'singleSelect') {
    body = (
      <SelectSubPanel
        initialValue={selectValues?.[active.value]}
        placeholder={active.placeholder}
        options={active.options ?? []}
        onCancel={dismissActive}
        onApply={(next) =>
          applyAndClose(() => onSelectApply?.(active.value, next))
        }
      />
    )
  }
  if (!body) return null
  return (
    <div
      key={active.value}
      ref={subRef}
      className={cn('absolute', SUBPANEL_SIDE_CLASSES[side])}
      style={{ top: activeOffset }}
    >
      {body}
    </div>
  )
}

export type { FiltersButtonProps, FilterGroup, FilterOption }
